/** Bytes above this are not previewed — the viewer is for reading, not for a dump. */
export const FILE_PREVIEW_MAX_BYTES = 2 * 1024 * 1024;

export type FilePreviewBody =
  | { readonly kind: "text"; readonly contents: string }
  | { readonly kind: "binary" }
  | { readonly kind: "too-large"; readonly bytes: number };

/**
 * Whether these bytes are text worth rendering.
 *
 * A NUL byte in the first few kilobytes is the classic binary tell, and it is
 * the one that matters here: a binary file decoded as UTF-8 renders as pages of
 * replacement characters, which reads as a corrupt file rather than as "this is
 * not previewable".
 */
export function looksBinary(bytes: Uint8Array): boolean {
  const window = Math.min(bytes.length, 8000);
  for (let index = 0; index < window; index += 1) {
    if (bytes[index] === 0) return true;
  }
  return false;
}

/**
 * What to render for a file the runtime just handed back.
 *
 * Size is checked before decoding: decoding a 50 MB file to discover it is too
 * large costs the same as showing it, which defeats the limit.
 */
export function resolveFilePreviewBody(
  bytes: Uint8Array,
  maxBytes: number = FILE_PREVIEW_MAX_BYTES,
): FilePreviewBody {
  if (bytes.length > maxBytes) return { kind: "too-large", bytes: bytes.length };
  if (looksBinary(bytes)) return { kind: "binary" };
  return { kind: "text", contents: new TextDecoder().decode(bytes) };
}

/**
 * The line to scroll to, clamped to the file.
 *
 * A reveal can outlive the content it was asked for — a link into line 900 of a
 * file that has since been truncated, or a stale surface restored against a
 * shorter file. Clamping keeps that a scroll to the end rather than a scroll to
 * nowhere. Lines are 1-based, matching the gutter and every editor link.
 */
export function clampRevealLine(line: number | null, lineCount: number): number | null {
  if (line === null || lineCount <= 0) return null;
  if (!Number.isFinite(line)) return null;
  const rounded = Math.floor(line);
  if (rounded < 1) return 1;
  return Math.min(rounded, lineCount);
}

/** How many lines the viewer will render. */
export function countLines(contents: string): number {
  if (contents.length === 0) return 0;
  let count = 1;
  for (let index = 0; index < contents.length; index += 1) {
    if (contents[index] === "\n") count += 1;
  }
  return count;
}

/**
 * The name shown in the tab and the header.
 *
 * The basename alone, because a path is too wide for a tab and the full path is
 * already the surface's identity. Deliberately not `basenameOfPath` from the
 * terminal-link helper: that one resolves OS-specific separators for links, and
 * a workspace-relative path is always POSIX.
 */
export function filePreviewTitle(relativePath: string): string {
  const trimmed = relativePath.replace(/\/+$/, "");
  const index = trimmed.lastIndexOf("/");
  return index === -1 ? trimmed : trimmed.slice(index + 1);
}
